import pluralize from "pluralize";
import { useState } from "react";
import { getEntityLabel } from "src/pages/ad-studio/insights/shared/channels";
import { DRILL_ROWS_PER_PAGE } from "src/pages/ad-studio/insights/performance/constants";
import type {
  DrillChildren,
  DrillChildrenAction,
  DrillScope,
  ParentGrain,
  MetricColumn,
  MetricKey,
} from "src/pages/ad-studio/insights/performance/types";
import {
  cubeLoad,
  useInsightsQuery,
} from "src/pages/ad-studio/insights/performance/use-insights-query";
import { defaultSortOf } from "src/pages/ad-studio/insights/performance/utils/meta";
import {
  channelOf,
  toChildList,
} from "src/pages/ad-studio/insights/performance/utils/drill";
import {
  CHILD_GRAIN,
  childViewOf,
  entityChildrenQuery,
} from "src/pages/ad-studio/insights/performance/utils/query";
import type { Order } from "src/pages/ad-studio/insights/performance/view/types";
import { usePaging } from "src/pages/ad-studio/insights/performance/view/use-paging";
import {
  columnSpecsOf,
  plannedColumns,
  sortedOrder,
  toListResult,
  toPage,
  toReportedMetrics,
} from "src/pages/ad-studio/insights/performance/view/utils";
import { toEntityList } from "src/pages/ad-studio/insights/performance/utils/embeds";

export function useDrillChildren({
  drill,
  grain,
  id,
}: {
  drill: DrillScope;
  grain: ParentGrain;
  id: string;
}): {
  state: DrillChildren;
  performAction: (action: DrillChildrenAction) => void;
} {
  const view = childViewOf(drill, grain);
  const [order, setOrder] = useState<Order<MetricKey>>({
    key: defaultSortOf(view),
    direction: "desc",
  });
  const list = toChildList({ drill, view });
  const childrenAt = (page: number) =>
    entityChildrenQuery({ drill, parent: { grain, id }, list, order, page });
  const paging = usePaging(JSON.stringify(childrenAt(0).query));
  const results = useInsightsQuery(
    cubeLoad({
      cube: childrenAt(paging.state.page),
      wire: drill.meta.views.ad.wire,
    }),
  );
  const childGrain = CHILD_GRAIN[grain];
  const childLabel = getEntityLabel(channelOf(drill), childGrain);
  const { metrics } = list;
  const toSpecs = (shown: readonly MetricColumn[]) =>
    columnSpecsOf({
      label: childLabel,
      meta: drill.meta,
      metrics: shown,
      order,
    });
  return {
    state: {
      heading: pluralize(childLabel),
      list: toListResult({
        result: results,
        planned: plannedColumns({
          groupBy: toEntityList({ grain: childGrain, meta: drill.meta }),
          specs: toSpecs(metrics),
        }),
        toPage: (rows) =>
          toPage({
            list: rows,
            specs: toSpecs(toReportedMetrics({ metrics, rows: rows.rows })),
            args: {
              paging: {
                page: paging.state.page,
                rowsPerPage: DRILL_ROWS_PER_PAGE,
              },
              noun: pluralize(childLabel.toLowerCase()),
              emptyMessage:
                "No results for the selected filters and date range.",
            },
          }),
      }),
    },
    performAction: (action) => {
      switch (action.kind) {
        case "DrillChildrenAction/sorted-column": {
          return setOrder((previous) =>
            sortedOrder({ order: previous, key: action.key }),
          );
        }
        case "DrillChildrenAction/paged": {
          return paging.performAction({
            kind: "PagingAction/paged",
            page: action.page,
          });
        }
        default: {
          return action satisfies never;
        }
      }
    },
  };
}
